// Shared constants + error-envelope parsers for the two Partner Brand
// surfaces — the multi-step create wizard (PartnerBrandCreateWizard) and the
// single-page edit form (PartnerBrandEditPage). Extracted so both stay in
// lockstep on the WCAG-passing defaults, slug hygiene, the color-token
// metadata, and the structured 400/409 channels the admin API returns.

import { ApiError } from "@/api/client";
import type {
  PartnerBrandColors,
  PartnerBrandSummary,
  PartnerBrandValidationResult,
} from "@/api/partnerBrands";

// ── color token metadata (role → label + helper) ───────────────────────────
export const COLOR_FIELDS: {
  key: keyof PartnerBrandColors;
  label: string;
  help: string;
}[] = [
  { key: "primary", label: "Primary", help: "Structure / chrome header" },
  { key: "foreground", label: "Foreground", help: "Headings / dark foreground" },
  { key: "pressed", label: "Pressed", help: "Pressed / footer" },
  { key: "background", label: "Background", help: "Page background" },
  { key: "surface", label: "Surface", help: "Section bands / cards" },
  { key: "accent", label: "Accent", help: "Buttons / active / rules" },
  { key: "bodyText", label: "Body text", help: "Body copy on the background" },
  { key: "hairline", label: "Hairline", help: "Hairlines / borders" },
];

/** Default color tokens — verified to pass every contrast target, so a
 *  wizard step-1 create with these never trips the WCAG guardrail. */
export const DEFAULT_COLORS: PartnerBrandColors = {
  primary: "#0A2540",
  foreground: "#111111",
  pressed: "#06182B",
  background: "#FFFFFF",
  surface: "#F5F3EE",
  accent: "#0A2540",
  bodyText: "#1A1A1A",
  hairline: "#D8D3C8",
};

export const DEFAULT_FONT_DISPLAY = "Cormorant Garamond";
export const DEFAULT_FONT_BODY = "Inter";

/** Slug hygiene as the user types — lowercase, non-kebab chars → hyphen. */
export const toSlug = (v: string) =>
  v.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/-{2,}/g, "-");

/** A #RRGGBB value the native color input can display (it can't show alpha). */
export const swatch = (hex: string) =>
  /^#[0-9a-fA-F]{6}/.test(hex) ? hex.slice(0, 7) : "#000000";

/** Structured server contrast errors ride the envelope's `details` channel. */
export function asValidationResult(
  err: unknown,
): PartnerBrandValidationResult | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const d = err.details as PartnerBrandValidationResult | undefined;
  return d && Array.isArray(d.messages) ? d : null;
}

/** A 409 carries the conflicting brand summary in `details`. */
export function asSlugConflict(err: unknown): PartnerBrandSummary | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const d = err.details as PartnerBrandSummary | undefined;
  return d && typeof d === "object" && typeof d.slug === "string" ? d : null;
}
